/**
 * Admin Home Group Edit Page: create or edit a home group.
 * Native mobile app style with collapsible sections.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconHomeHeart,
  IconAlertTriangle,
  IconClock,
  IconUsers,
  IconTrash,
  IconPlus,
  IconCamera,
  IconPhoto,
  IconCalendar,
} from "../../../components/Icons.tsx";
import {
  getTelegram,
  haptic,
  safeShowConfirm,
  safeShowAlert,
} from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import { getAuthHeaders, getErrorMessage } from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";
import {
  useHomeGroup,
  useCreateHomeGroup,
  useUpdateHomeGroup,
  useDeleteHomeGroup,
  useAddHomeGroupSchedule,
  useDeleteHomeGroupSchedule,
} from "./hooks";
import { useCreateEvent } from "../events/hooks";
import { CollapsibleSection } from "../events/components/CollapsibleSection.tsx";
import { FullscreenTextEditor } from "../events/components/FullscreenTextEditor.tsx";
import {
  SectionLabel,
  InlineTextField,
  InlineTextPreview,
  InlineStepper,
  InlineToggle,
  InlinePickerRow,
  ExpandablePicker,
  InlineLinkField,
} from "../events/components/InlineFields.tsx";
import { AccountPicker } from "./components/AccountPicker.tsx";
import {
  type AdminAccountRef,
  type HomeGroupScheduleItem,
  type DayOfWeek,
  DAY_OF_WEEK_LABELS,
  formatAccountName,
} from "./types.ts";


type GenderInput = "MALE" | "FEMALE" | "ANY";
const GENDER_OPTIONS: { value: GenderInput; label: string }[] = [
  { value: "ANY", label: "Любой" },
  { value: "MALE", label: "Мужской" },
  { value: "FEMALE", label: "Женский" },
];

const DAY_OPTIONS: DayOfWeek[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

interface HomeGroupFormData {
  name: string;
  description: string;
  leader: AdminAccountRef | null;
  coLeader: AdminAccountRef | null;
  location: string;
  address: string;
  mapsUrl: string;
  maxCapacity: number | "";
  genderRestriction: GenderInput;
  minAge: number | "";
  maxAge: number | "";
  isActive: boolean;
  isOpen: boolean;
  imageUrl: string;
}

const emptyForm: HomeGroupFormData = {
  name: "",
  description: "",
  leader: null,
  coLeader: null,
  location: "",
  address: "",
  mapsUrl: "",
  maxCapacity: "",
  genderRestriction: "ANY",
  minAge: "",
  maxAge: "",
  isActive: true,
  isOpen: true,
  imageUrl: "",
};

export function AdminHomeGroupEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();
  useAuth();
  const isNew = id === "new";

  // URQL hooks
  const {
    homeGroup,
    isLoading: isLoadingGroup,
    error: groupError,
    refetch: refetchGroup,
  } = useHomeGroup(isNew ? undefined : id);
  const { createHomeGroup, isLoading: isCreatingGroup } = useCreateHomeGroup();
  const { updateHomeGroup, isLoading: isUpdatingGroup } = useUpdateHomeGroup();
  const { deleteHomeGroup, isLoading: isDeletingGroup } = useDeleteHomeGroup();
  const { addSchedule: addScheduleMutation } = useAddHomeGroupSchedule();
  const { deleteSchedule: deleteScheduleMutation } =
    useDeleteHomeGroupSchedule();
  const { createEvent, isLoading: isCreatingEvent } = useCreateEvent();

  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<HomeGroupFormData>(emptyForm);
  const [schedules, setSchedules] = useState<HomeGroupScheduleItem[]>([]);
  const [members, setMembers] = useState<
    { id: string; account: AdminAccountRef; role: string; isActive: boolean }[]
  >([]);
  const [editingField, setEditingField] = useState<"description" | null>(null);
  const [showGenderPicker, setShowGenderPicker] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Derived states
  const isLoading = isNew ? false : isLoadingGroup;
  const isSaving = isCreatingGroup || isUpdatingGroup;
  const isDeleting = isDeletingGroup;

  // Sync home group data to form
  useEffect(() => {
    if (homeGroup) {
      setForm({
        name: homeGroup.name,
        description: homeGroup.description ?? "",
        leader: homeGroup.leader,
        coLeader: homeGroup.coLeader,
        location: homeGroup.location ?? "",
        address: homeGroup.address ?? "",
        mapsUrl: homeGroup.mapsUrl ?? "",
        maxCapacity: homeGroup.maxCapacity ?? "",
        genderRestriction: homeGroup.genderRestriction as GenderInput,
        minAge: homeGroup.minAge ?? "",
        maxAge: homeGroup.maxAge ?? "",
        isActive: homeGroup.isActive,
        isOpen: homeGroup.isOpen,
        imageUrl: homeGroup.imageUrl ?? "",
      });
      setSchedules(homeGroup.schedules ?? []);
      setMembers(
        (homeGroup.members ?? []).map((m) => ({
          id: m.id,
          account: m.account,
          role: m.role,
          isActive: m.isActive,
        })),
      );
    }
  }, [homeGroup]);

  // Sync group error
  useEffect(() => {
    if (groupError) {
      setError(groupError.message || "Группа не найдена");
    }
  }, [groupError]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/home-groups");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, isNew]);

  const updateForm = useCallback(
    <K extends keyof HomeGroupFormData>(
      key: K,
      value: HomeGroupFormData[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSave = async () => {
    if (!form.name.trim()) {
      safeShowAlert("Введите название группы");
      return;
    }
    if (!form.leader) {
      safeShowAlert("Выберите лидера группы");
      return;
    }
    setError(null);
    try {
      const input = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        leaderId: form.leader.id,
        coLeaderId: form.coLeader?.id ?? null,
        location: form.location.trim() || null,
        address: form.address.trim() || null,
        mapsUrl: form.mapsUrl.trim() || null,
        maxCapacity:
          form.maxCapacity === "" ? null : Number(form.maxCapacity) || null,
        genderRestriction: form.genderRestriction,
        minAge: form.minAge === "" ? null : Number(form.minAge) || null,
        maxAge: form.maxAge === "" ? null : Number(form.maxAge) || null,
        isActive: form.isActive,
        isOpen: form.isOpen,
        imageUrl: form.imageUrl.trim() || null,
      };

      if (isNew) {
        const newId = await createHomeGroup(input);
        haptic("success");
        for (const s of schedules) {
          await addScheduleMutation(newId, {
            dayOfWeek: s.dayOfWeek,
            startTime: s.startTime,
            endTime: s.endTime || null,
            isActive: s.isActive,
          });
        }
        navigate(`/admin/home-groups/${newId}`, { replace: true });
      } else {
        if (!id) {
          setError("ID группы не найден");
          return;
        }
        await updateHomeGroup(id, input);
        for (const s of schedules) {
          if (s.id.startsWith("new-")) {
            await addScheduleMutation(id, {
              dayOfWeek: s.dayOfWeek,
              startTime: s.startTime,
              endTime: s.endTime || null,
              isActive: s.isActive,
            });
          }
        }
        haptic("success");
        refetchGroup();
      }
    } catch (err) {
      setError(getErrorMessage(err));
      haptic("error");
    }
  };

  const handleDelete = () => {
    if (isNew || !id) return;
    safeShowConfirm(
      "Удалить домашнюю группу? Это действие нельзя отменить.",
      async (ok) => {
        if (!ok) return;
        setError(null);
        try {
          await deleteHomeGroup(id);
          haptic("success");
          navigate("/admin/home-groups");
        } catch (err) {
          setError(getErrorMessage(err));
          haptic("error");
        }
      },
    );
  };

  const handleImageUpload = async (file: File) => {
    if (!id || isNew) return;
    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/home-group-image/${id}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(response, "home group image upload");
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = await response.json() as { url: string };
      updateForm("imageUrl", result.url);
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const addSchedule = () => {
    haptic("light");
    setSchedules((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        dayOfWeek: "MONDAY",
        startTime: "19:00",
        endTime: null,
        isActive: true,
      },
    ]);
  };

  const updateSchedule = (
    index: number,
    field: keyof HomeGroupScheduleItem,
    value: string | boolean | null,
  ) => {
    setSchedules((prev) => {
      const next = [...prev];
      const s = { ...next[index], [field]: value };
      next[index] = s;
      return next;
    });
  };

  const removeSchedule = async (index: number) => {
    haptic("light");
    const s = schedules[index];
    if (s.id.startsWith("new-")) {
      setSchedules((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      await deleteScheduleMutation(s.id);
      setSchedules((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      safeShowAlert(getErrorMessage(err));
    }
  };

  if (isLoading) return <PageLoader message="Загрузка..." />;

  if (error && !isNew) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Ошибка</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <button onClick={refetchGroup} className="btn-primary">
            Повторить
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center text-rose-600">
            <IconHomeHeart className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">
              {isNew ? "Новая группа" : form.name || "Группа"}
            </h1>
            <p className="text-[13px] text-slate-500">
              {isNew ? "Создание домашней группы" : "Редактирование"}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-sm">
            {error}
          </div>
        )}

        {/* Кнопка «Создать мероприятие» — только для существующей группы */}
        {!isNew && id && (
          <div className="mb-5">
            <button
              type="button"
              onClick={async () => {
                if (isCreatingEvent) return;
                try {
                  const result = await createEvent({
                    title: "Новое мероприятие",
                    startsAt: new Date().toISOString(),
                    status: "DRAFT",
                    homeGroupId: id,
                    requiresRsvpConfirmation: true,
                  });
                  haptic("success");
                  navigate(`/admin/events/${result.id}`);
                } catch (err) {
                  haptic("error");
                  safeShowAlert(getErrorMessage(err));
                }
              }}
              disabled={isCreatingEvent}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-medium text-[15px] transition-colors disabled:opacity-50"
            >
              {isCreatingEvent ? (
                <span className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
              ) : (
                <IconCalendar className="w-5 h-5" />
              )}
              {isCreatingEvent ? "Создание…" : "Создать мероприятие"}
            </button>
          </div>
        )}

        {/* Основное */}
        <SectionLabel>Основное</SectionLabel>
        <div className="card overflow-hidden mb-5">
          <InlineTextField
            label="Название"
            value={form.name}
            onChange={(v) => updateForm("name", v)}
            placeholder="Название группы"
            required
          />
          <InlineTextPreview
            label="Описание"
            value={form.description}
            placeholder="Краткое описание..."
            onClick={() => setEditingField("description")}
          />
        </div>

        {/* Медиа — обложка (только для существующей группы) */}
        {!isNew && (
          <>
            <SectionLabel>Медиа</SectionLabel>
            <div className="card overflow-hidden mb-5">
              <div className="p-4">
                <p className="text-[13px] text-slate-500 mb-3">Обложка</p>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isUploadingImage) imageInputRef.current?.click();
                  }}
                  disabled={isUploadingImage}
                  className="relative w-full aspect-[2/1] rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 active:border-slate-300 transition-colors disabled:opacity-90 flex items-center justify-center"
                >
                  {form.imageUrl && !isUploadingImage ? (
                    <>
                      <img
                        src={
                          imageCacheBust
                            ? `${form.imageUrl}${form.imageUrl.includes("?") ? "&" : "?"}_t=${imageCacheBust}`
                            : form.imageUrl
                        }
                        alt="Обложка"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <span className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-black/50 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                        <IconCamera className="w-4 h-4" />
                        Заменить
                      </span>
                    </>
                  ) : isUploadingImage ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                      <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                      <span className="text-[13px]">Загрузка…</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                      <IconPhoto className="w-10 h-10" />
                      <span className="text-[13px]">Добавить обложку</span>
                    </div>
                  )}
                </button>
                {form.imageUrl && !isUploadingImage && (
                  <button
                    type="button"
                    onClick={() => updateForm("imageUrl", "")}
                    className="mt-2 flex items-center gap-1 text-[13px] text-red-500 active:text-red-600"
                  >
                    <IconTrash className="w-4 h-4" />
                    Удалить
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Лидеры */}
        <SectionLabel>Лидеры</SectionLabel>
        <div className="card overflow-hidden mb-5">
          <AccountPicker
            label="Лидер"
            value={form.leader}
            onChange={(a) => updateForm("leader", a)}
            placeholder="Выберите лидера"
            excludeId={form.coLeader?.id}
          />
          <AccountPicker
            label="Солидер"
            value={form.coLeader}
            onChange={(a) => updateForm("coLeader", a)}
            placeholder="Выберите солидера (необязательно)"
            excludeId={form.leader?.id}
          />
        </div>

        {/* Место */}
        <SectionLabel>Место</SectionLabel>
        <div className="card overflow-hidden mb-5">
          <InlineTextField
            label="Район"
            value={form.location}
            onChange={(v) => updateForm("location", v)}
            placeholder="Например: Чалонг"
          />
          <InlineTextField
            label="Адрес"
            value={form.address}
            onChange={(v) => updateForm("address", v)}
            placeholder="Полный адрес для участников"
          />
          <InlineLinkField
            label="Ссылка на карты"
            value={form.mapsUrl}
            onChange={(v) => updateForm("mapsUrl", v)}
            placeholder="https://maps.google.com/..."
          />
        </div>

        {/* Ограничения */}
        <SectionLabel>Ограничения</SectionLabel>
        <div className="card overflow-hidden mb-5">
          <InlineStepper
            label="Макс. участников"
            value={
              form.maxCapacity === ""
                ? null
                : typeof form.maxCapacity === "number"
                  ? form.maxCapacity
                  : null
            }
            onChange={(v) => updateForm("maxCapacity", v === null ? "" : v)}
            min={1}
            placeholder="Нет"
          />
          <InlinePickerRow
            label="Пол"
            value={
              GENDER_OPTIONS.find((o) => o.value === form.genderRestriction)
                ?.label ?? form.genderRestriction
            }
            onClick={() => setShowGenderPicker(!showGenderPicker)}
            isOpen={showGenderPicker}
          />
          <ExpandablePicker
            isOpen={showGenderPicker}
            options={GENDER_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            value={form.genderRestriction}
            onChange={(v) => {
              updateForm("genderRestriction", v as GenderInput);
              setShowGenderPicker(false);
            }}
            onClose={() => setShowGenderPicker(false)}
          />
          <InlineStepper
            label="Мин. возраст"
            value={
              form.minAge === ""
                ? null
                : typeof form.minAge === "number"
                  ? form.minAge
                  : null
            }
            onChange={(v) => updateForm("minAge", v === null ? "" : v)}
            min={0}
            placeholder="—"
          />
          <InlineStepper
            label="Макс. возраст"
            value={
              form.maxAge === ""
                ? null
                : typeof form.maxAge === "number"
                  ? form.maxAge
                  : null
            }
            onChange={(v) => updateForm("maxAge", v === null ? "" : v)}
            min={0}
            placeholder="—"
          />
        </div>

        {/* Статус */}
        <SectionLabel>Статус</SectionLabel>
        <div className="card overflow-hidden mb-5">
          <InlineToggle
            label="Активна"
            checked={form.isActive}
            onChange={(v) => updateForm("isActive", v)}
          />
          <InlineToggle
            label="Открыта для новых участников"
            checked={form.isOpen}
            onChange={(v) => updateForm("isOpen", v)}
          />
        </div>

        {/* Расписание */}
        <CollapsibleSection
          title="Расписание"
          defaultOpen={true}
          icon={<IconClock className="w-5 h-5" />}
          badge={
            schedules.length > 0 ? (
              <span className="text-[11px] text-slate-500">
                {schedules.length}
              </span>
            ) : undefined
          }
        >
          <div className="space-y-2">
            {schedules.map((s, index) => (
              <div
                key={s.id}
                className="flex flex-wrap items-center gap-2 min-h-[52px] px-4 py-2 rounded-xl bg-slate-50 border border-slate-100"
              >
                <select
                  value={s.dayOfWeek}
                  onChange={(e) =>
                    updateSchedule(
                      index,
                      "dayOfWeek",
                      e.target.value as DayOfWeek,
                    )
                  }
                  className="flex-1 min-w-[100px] px-3 py-2 text-[15px] rounded-lg border border-slate-200 bg-white outline-none"
                >
                  {DAY_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {DAY_OF_WEEK_LABELS[d]}
                    </option>
                  ))}
                </select>
                <input
                  type="time"
                  value={s.startTime}
                  onChange={(e) =>
                    updateSchedule(index, "startTime", e.target.value)
                  }
                  className="w-24 px-3 py-2 text-[15px] rounded-lg border border-slate-200 bg-white outline-none"
                />
                <input
                  type="time"
                  value={s.endTime ?? ""}
                  onChange={(e) =>
                    updateSchedule(index, "endTime", e.target.value || null)
                  }
                  className="w-24 px-3 py-2 text-[15px] rounded-lg border border-slate-200 bg-white outline-none"
                  placeholder="до"
                />
                <button
                  type="button"
                  onClick={() => removeSchedule(index)}
                  className="p-2 rounded-lg text-slate-400 active:bg-slate-200 active:text-red-600 transition-colors"
                  aria-label="Удалить"
                >
                  <IconTrash className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addSchedule}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-slate-300 text-slate-500 text-[14px] font-medium active:bg-slate-50 transition-colors"
            >
              <IconPlus className="w-4 h-4" />
              Добавить время
            </button>
          </div>
        </CollapsibleSection>

        {/* Участники (только просмотр) */}
        {!isNew && members.length > 0 && (
          <CollapsibleSection
            title="Участники"
            defaultOpen={false}
            icon={<IconUsers className="w-5 h-5" />}
            badge={
              <span className="text-[11px] text-slate-500">
                {members.length}
              </span>
            }
          >
            <ul className="divide-y divide-slate-100">
              {members.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between min-h-[52px] px-4 py-3"
                >
                  <span className="text-[15px] text-slate-900">
                    {formatAccountName(m.account)}
                  </span>
                  <span className="text-[13px] text-slate-500">
                    {m.role === "LEADER"
                      ? "Лидер"
                      : m.role === "CO_LEADER"
                        ? "Солидер"
                        : "Участник"}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {/* Fullscreen text editor for description */}
        {editingField === "description" && (
          <FullscreenTextEditor
            isOpen={true}
            title="Описание"
            value={form.description}
            placeholder="Краткое описание группы..."
            onSave={(v) => {
              updateForm("description", v);
              setEditingField(null);
            }}
            onClose={() => setEditingField(null)}
          />
        )}

        {/* Действия */}
        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full py-3 rounded-xl bg-[var(--color-accent)] text-white font-semibold text-[15px] active:scale-[0.99] disabled:opacity-50"
          >
            {isSaving ? "Сохранение..." : "Сохранить"}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full py-3 rounded-xl border border-red-200 text-red-600 font-semibold text-[15px] active:scale-[0.99] disabled:opacity-50"
            >
              {isDeleting ? "Удаление..." : "Удалить группу"}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
